import { z } from 'zod';
export const PayoutStatusSchema = z.enum([
    'pending', // session just completed, waiting for payment
    'paid', // driver paid, owner payout processed
    'payment_pending', // session stopped, driver has no payment method
    'payment_failed', // payment intent failed
    'transferred', // Stripe transfer to operator's Connect account succeeded
    'failed', // transfer failed (will retry)
    'refunded', // session was refunded
    'disputed', // cardholder disputed the charge (Phase 8)
    'test_written_off', // Operator-approved test exception; no captured payment.
]);
/**
 * Per-session payout breakdown.
 * All monetary amounts in CENTS (integer) except rates.
 *
 * Math pipeline (matches stg-admin):
 *   gross_amount_cents                 = driver pays (from tariff)
 *   - stripe_fee_cents (2.9% + 30)    = Stripe processing fee
 *   = net_revenue_cents
 *   - electricity_cost_cents          = location rate × session kWh
 *   = revenue_after_electricity_cents
 *   - platform_fee_cents              = EVI's take (see below)
 *   = owner_payout_cents              = what the station owner gets
 *
 * Tier 2 (Warren, 2026-09-26): platform_fee_cents is
 *   connection_fee_cents + (the percentage part) - evi_funded_discount_cents
 * The connection fee is EVI's and never shared; the percentage part is
 * 15% (the default) of the split base with the fee taken out first; an
 * EVI coupon or points credit comes out of the percentage part only, so
 * the host is paid as if the session were undiscounted.
 */
export const PayoutBreakdownSchema = z.object({
    site_fee_cents: z.number().int().nonnegative().optional(),
    site_fee_tax_cents: z.number().int().nonnegative().optional(),
    site_processing_cents: z.number().int().nonnegative().optional(),
    site_share_cents: z.number().int().nonnegative().optional(),
    standard_gross_cents: z.number().int().nonnegative().optional(),
    gross_amount_cents: z.number().int().nonnegative(),
    stripe_fee_cents: z.number().int().nonnegative(),
    net_revenue_cents: z.number().int().nonnegative(),
    electricity_cost_cents: z.number().int().nonnegative(),
    revenue_after_electricity_cents: z.number().int().nonnegative(),
    platform_fee_cents: z.number().int().nonnegative(),
    /** What the station operator receives, in cents.
     *  For tenant-managed chargers this is the operator's full share.
     *  For resident-managed chargers this is what's left for the
     *  building (residual after the resident's markup is split off);
     *  expect ~0 in normal cases when markup eats the whole thing. */
    owner_payout_cents: z.number().int().nonnegative(),
    /** Resident-managed only — markup paid to the charger's
     *  resident owner via a secondary Stripe transfer (Phase 6.3).
     *  Default 0 for tenant-managed chargers and for sessions with
     *  guest_markup_bps null/0 / owner self-charge. */
    resident_markup_cents: z.number().int().nonnegative().default(0),
    /** EVI's connection fee billed on this session, kept by EVI whole and
     *  included in platform_fee_cents. 0 on a session with no pinned fee
     *  (legacy) or on one that could not bill (free charger). */
    connection_fee_cents: z.number().int().nonnegative().default(0),
    /** An EVI coupon or points credit applied to this session and funded
     *  from EVI's percentage part. gross_amount_cents is what the card was
     *  charged (after it); the host's legs are computed before it. */
    evi_funded_discount_cents: z.number().int().nonnegative().default(0),
    /** Revenue split percentage applied (0-100). */
    platform_percentage: z.number().min(0).max(100),
    /** Electricity rate used, in cents/kWh */
    electricity_rate_cents: z.number().nonnegative(),
    /** Session kWh for reference */
    energy_kwh: z.number().nonnegative(),
    currency: z.string().length(3),
});
/** Status of the secondary transfer to a resident's Express
 *  account. Distinct from the primary Payout.status which
 *  tracks the destination charge to the building. Null for
 *  tenant-managed chargers (no secondary transfer ever happens). */
export const ResidentTransferStatusSchema = z.enum([
    'pending', // capture succeeded; transfer not yet initiated
    'transferred', // Stripe transfers.create succeeded
    'failed', // transfer failed; manual reconciliation needed
    'reversed', // dispute reversed the transfer (Phase 8)
]);
export const PayoutSchema = z.object({
    _id: z.string(),
    session_id: z.string(),
    tenant_id: z.string(),
    charger_id: z.string(),
    location_id: z.string(),
    user_id: z.string().optional(),
    status: PayoutStatusSchema,
    breakdown: PayoutBreakdownSchema,
    stripe_payment_intent_id: z.string().optional(),
    stripe_transfer_id: z.string().optional(),
    /** Resident-managed only — Stripe Transfer id moving the
     *  resident's markup from the building's Connect account to the
     *  resident's Express. Null on tenant-managed payouts. */
    resident_transfer_id: z.string().nullable().optional(),
    resident_transfer_status: ResidentTransferStatusSchema.nullable().optional(),
    retry_count: z.number().int().nonnegative(),
    next_retry_at: z.coerce.date().optional(),
    error_message: z.string().optional(),
    paid_at: z.coerce.date().optional(),
    transferred_at: z.coerce.date().optional(),
    created_at: z.coerce.date(),
    updated_at: z.coerce.date(),
});
//# sourceMappingURL=payout.js.map